/**
 * Composable — Notifications centralisées (wrapper SweetAlert2)
 *
 * Phase 8 — Système de notifications centralisé
 *
 * Centralise tous les appels Swal.fire() pour :
 * - API cohérente dans tout le codebase
 * - i18n intégré (passe des clés de traduction)
 * - Options par défaut homogènes
 * - Point unique pour changer la lib de notification si besoin
 *
 * Usage dans un composant :
 *   import { useNotifications } from '@/composables'
 *
 *   setup() {
 *     const { success, error, confirm, input, warning } = useNotifications()
 *     return { success, error, confirm, input, warning }
 *   }
 *
 * Usage dans un fichier non-composant (service, store) :
 *   import { notify } from '@/composables/useNotifications'
 *   notify.success('Opération réussie')
 */
import Swal from 'sweetalert2'

// ──────────────────────────────────────────────
// Helpers internes
// ──────────────────────────────────────────────

const defaultOptions = {
  heightAuto: false,
  confirmButtonColor: '#4CAF50',
}

const errorButtonColor = '#DD6B55'

// ──────────────────────────────────────────────
// API publique — fonctions pures (sans composable)
// Utilisable partout : composants, services, stores
// ──────────────────────────────────────────────

/**
 * Notification de succès
 * @param {string} title - Texte ou clé i18n
 * @param {Object} [options] - Options Swal additionnelles
 */
const success = (title, options = {}) =>
  Swal.fire({
    ...defaultOptions,
    icon: 'success',
    title,
    timer: 2500,
    showConfirmButton: false,
    ...options,
  })

/**
 * Notification d'erreur
 * @param {string} text - Message d'erreur
 * @param {Object} [options] - Options Swal additionnelles
 */
const error = (text, options = {}) =>
  Swal.fire({
    ...defaultOptions,
    icon: 'error',
    text,
    confirmButtonColor: errorButtonColor,
    ...options,
  })

/**
 * Notification d'avertissement (info)
 * @param {string} title - Titre
 * @param {string} [text] - Texte détaillé
 * @param {Object} [options] - Options Swal additionnelles
 */
const warning = (title, text = '', options = {}) =>
  Swal.fire({
    ...defaultOptions,
    icon: 'warning',
    title,
    text,
    ...options,
  })

/**
 * Notification d'information
 * @param {string} title - Titre
 * @param {string} [text] - Texte détaillé
 * @param {Object} [options] - Options Swal additionnelles
 */
const info = (title, text = '', options = {}) =>
  Swal.fire({
    ...defaultOptions,
    icon: 'info',
    title,
    text,
    ...options,
  })

/**
 * Dialogue de confirmation (Oui/Non)
 * @param {string} title - Question à confirmer
 * @param {Object} [options] - Options Swal additionnelles
 * @returns {Promise<SweetAlertResult>} - result.isConfirmed === true si accepté
 */
const confirm = (title, options = {}) =>
  Swal.fire({
    ...defaultOptions,
    icon: 'warning',
    title,
    showCancelButton: true,
    confirmButtonColor: errorButtonColor,
    ...options,
  })

/**
 * Dialogue avec champ de saisie
 * @param {string} title - Titre
 * @param {Object} [options] - Options Swal (input, inputLabel, inputValidator, etc.)
 * @returns {Promise<SweetAlertResult>} - result.value contient la saisie
 */
const input = (title, options = {}) =>
  Swal.fire({
    ...defaultOptions,
    title,
    input: 'text',
    showCancelButton: true,
    ...options,
  })

/**
 * Affiche le spinner de chargement dans la popup active
 * Utile dans un inputValidator async pour indiquer que le traitement est en cours
 */
const showLoading = () => Swal.showLoading()

// ──────────────────────────────────────────────
// Export objet (pour usage hors composable)
// ──────────────────────────────────────────────

export const notify = {
  success,
  error,
  warning,
  info,
  confirm,
  input,
  showLoading,
}

// ──────────────────────────────────────────────
// Composable (pour usage dans setup())
// ──────────────────────────────────────────────

export function useNotifications () {
  return {
    success,
    error,
    warning,
    info,
    confirm,
    input,
  }
}
